import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiLock, FiMinus, FiPlus, FiRefreshCw, FiTruck } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";

const formatPrice = (price) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: siteConfig.currency,
  }).format(Number(price) || 0);

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    clearCart,
    removeFromCart,
    updateQuantity,
  } = useContext(CartContext);

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === Number(item.id)),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + Number(item.product.price) * Number(item.quantity),
    0
  );
  const shipping = siteConfig.shippingFlatRate;
  const total = subtotal + shipping;

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (detailedItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <div className="cart-empty">
          <span>Your bag</span>
          <h1>Your ritual is waiting.</h1>
          <p>
            Explore plant-powered hair and body care made for your texture,
            goals and everyday rhythm.
          </p>
          <Link to="/shop" onClick={toTop}>Shop the collection</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <span>Shopping bag</span>
        <h1>Your ritual</h1>
        <p>{detailedItems.length} {detailedItems.length === 1 ? "selection" : "selections"}, thoughtfully gathered.</p>
      </header>

      <div className="cart-page__layout">
        <section className="cart-products" aria-labelledby="cart-items-title">
          <div className="cart-products__topline">
            <h2 id="cart-items-title">Your selections</h2>
            <button type="button" onClick={clearCart}>Clear bag</button>
          </div>

          <div className="cart-items">
            {detailedItems.map((item) => {
              const { product } = item;
              const itemKey = `${item.id}-${item.size}-${item.color}`;
              const lineTotal = Number(product.price) * Number(item.quantity);

              return (
                <article className="cart-item" key={itemKey}>
                  <Link
                    className="cart-item__image"
                    to={`/shopDetails/${product.id}`}
                    onClick={toTop}
                    aria-label={`View ${product.name}`}
                  >
                    <img src={product.images[0]} alt={product.name} />
                  </Link>

                  <div className="cart-item__content">
                    <p className="cart-item__category">{product.category}</p>
                    <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
                      {product.name}
                    </Link>
                    <p className="cart-item__subtitle">{product.subtitle}</p>
                    <dl>
                      <div><dt>Size</dt><dd>{item.size}</dd></div>
                      <div><dt>Natural notes</dt><dd>{item.color}</dd></div>
                    </dl>

                    <button
                      type="button"
                      className="cart-item__remove"
                      onClick={() => removeFromCart(item.id, item.size, item.color)}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="cart-item__aside">
                    <strong>{formatPrice(lineTotal)}</strong>
                    <div className="cart-quantity" aria-label={`Quantity for ${product.name}`}>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            Math.max(1, Number(item.quantity) - 1)
                          )
                        }
                        aria-label={`Decrease ${product.name} quantity`}
                        disabled={Number(item.quantity) <= 1}
                      >
                        <FiMinus aria-hidden="true" />
                      </button>
                      <span aria-live="polite">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            Math.min(10, Number(item.quantity) + 1)
                          )
                        }
                        aria-label={`Increase ${product.name} quantity`}
                        disabled={Number(item.quantity) >= 10}
                      >
                        <FiPlus aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <Link className="cart-continue" to="/shop" onClick={toTop}>
            ← Continue shopping
          </Link>
        </section>

        <aside className="cart-summary" aria-labelledby="summary-title">
          <h2 id="summary-title">Order summary</h2>

          <div className="cart-shipping-progress">
            <p>Tracked shipping is a fixed {formatPrice(shipping)} per order.</p>
            <div aria-hidden="true">
              <span style={{ width: "100%" }} />
            </div>
          </div>

          <dl className="cart-summary__totals">
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Estimated shipping</dt><dd>{formatPrice(shipping)}</dd></div>
            <div className="cart-summary__total"><dt>Total</dt><dd>{formatPrice(total)} <small>{siteConfig.currency}</small></dd></div>
          </dl>

          <button
            type="button"
            className="cart-checkout"
            onClick={() => {
              navigate("/checkout");
              toTop();
            }}
          >
            Secure checkout <FiLock aria-hidden="true" />
          </button>
          <p className="cart-summary__tax-note">Taxes are calculated at checkout where applicable.</p>

          <div className="cart-summary__services">
            <p><FiTruck aria-hidden="true" /><span><strong>Thoughtful delivery</strong>Plastic-conscious packaging and tracked shipping.</span></p>
            <p><FiRefreshCw aria-hidden="true" /><span><strong>Easy returns</strong>Eligible unopened items within {siteConfig.policies.returnWindow} days.</span></p>
          </div>

          <div className="cart-payment-note">
            <FiLock aria-hidden="true" />
            <span>Secure checkout · Visa · Mastercard · Amex</span>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
